import { useMemo, useState } from 'react';

import { Select, Spin } from 'antd';
import type { SelectProps } from 'antd';

import { stripHtmlTags } from '@/shared/lib/text';

import type { AssetSearchResult } from '@/entities/asset-search/api/searchAssetsByTypeApi';
import { useInfiniteAssetSearch } from '@/entities/asset-search/model/useInfiniteAssetSearch';

import styles from './InfiniteAssetSelect.module.css';

export type AssetSearchResultData = AssetSearchResult & {
  runId?: number;
};

type InfiniteAssetSelectProps = Omit<SelectProps, 'options' | 'onSearch'> & {
  assetType: 'dataset' | 'feature_set' | 'run_parameter_set' | string[];
  onSelectAsset?: (assetData: AssetSearchResultData) => void;
  initialOption?: {
    id: number;
    title: string;
    description?: string;
    runId?: number;
    type?: string;
  };
};

export const InfiniteAssetSelect = ({
  assetType,
  value,
  onChange,
  onSelectAsset,
  initialOption,
  ...restProps
}: InfiniteAssetSelectProps) => {
  const [searchValue, setSearchValue] = useState('');

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteAssetSearch({
    assetTypes: assetType,
    query: searchValue || undefined,
  });

  const allResults = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) => page.results);
  }, [data]);

  const options = useMemo(() => {
    const uniqueMap = new Map();

    if (initialOption) {
      const initialKey = `${initialOption.id}`;
      const cleanDescription = initialOption.description ? stripHtmlTags(initialOption.description) : null;

      uniqueMap.set(initialKey, {
        value: initialOption.title,
        label: (
          <div className={styles.optionWrapper}>
            <div className={styles.optionTitle}>{initialOption.title}</div>
            {cleanDescription && <div className={styles.optionDescription}>{cleanDescription}</div>}
          </div>
        ),
        key: initialKey,
        id: initialOption.id,
        fullData: initialOption,
      });
    }

    if (data) {
      allResults.forEach((result) => {
        const uniqueKey = `${result.id}`;

        if (!uniqueMap.has(uniqueKey)) {
          const cleanDescription = result.description ? stripHtmlTags(result.description) : null;

          let displayTitle = result.title;
          if (result.type === 'feature_set' || result.type === 'run_feature_set') {
            displayTitle = result.featureSet?.table || result.title;
          } else if (result.type === 'dataset' || result.type === 'run_dataset') {
            displayTitle = result.dataset?.table || result.title;
          }

          uniqueMap.set(uniqueKey, {
            value: displayTitle,
            label: (
              <div className={styles.optionWrapper}>
                <div className={styles.optionTitle}>{displayTitle}</div>
                {cleanDescription && <div className={styles.optionDescription}>{cleanDescription}</div>}
              </div>
            ),
            key: uniqueKey,
            id: result.id,
            fullData: result,
          });
        }
      });
    }

    return Array.from(uniqueMap.values());
  }, [data, allResults, initialOption]);

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelect = (value: string, option: any) => {
    if (option?.fullData && onSelectAsset) {
      onSelectAsset(option.fullData);
    }
    if (onChange) {
      onChange(value, option);
    }
  };

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 10;

    if (isNearBottom && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  return (
    <Select
      {...restProps}
      value={value}
      onChange={onChange}
      onSelect={handleSelect}
      onSearch={handleSearch}
      onPopupScroll={handlePopupScroll}
      options={options}
      showSearch
      filterOption={false}
      virtual
      labelRender={(props) => <span>{props.value}</span>}
      notFoundContent={isLoading ? <Spin size="small" /> : '검색 결과가 없습니다'}
      popupRender={(menu) => (
        <>
          {menu}
          {isFetchingNextPage && (
            <div className={styles.loadingWrapper}>
              <Spin size="small" />
            </div>
          )}
        </>
      )}
    />
  );
};
